<template>
  <li class="hospitalCassetteSeminar">
    <div>
      <div class="hospitalCassetteSeminar_inner">
        <div
          class="hospitalCassetteSeminar_header"
          :class="{ hasCheckbox: canRequestCheck }"
        >
          <p class="hospitalCassetteSeminar_hospital">
            {{ detail.hospital_name }}
          </p>
          <p class="hospitalCassetteSeminar_ttl">
            {{ detail.program }}
          </p>
          <HospitalLabel :showTraining="false" :items="data.labels" />
        </div>

        <div class="hospitalCassetteSeminar_img -isPC">
          <img
            v-if="detail.basicinfo_image1"
            :src="detail.basicinfo_image1"
            :alt="detail.hospital_name"
          />
          <img
            v-else
            src="@/assets/images/common/noimg.svg"
            :alt="detail.hospital_name"
          />
        </div>

        <div class="hospitalCassetteSeminar_info">
          <div class="hospitalCassetteSeminar_address">
            {{ detail.prefectures }}{{ detail.hospital_address }}
          </div>
          <div class="hospitalCassetteSeminar_class">
            {{ detail.hospital_class }}
          </div>
          <div class="hospitalCassetteSeminar_img">
            <img
              v-if="detail.basicinfo_image1"
              :src="detail.basicinfo_image1"
              :alt="detail.hospital_name"
            />
            <img
              v-else
              src="@/assets/images/common/noimg.svg"
              :alt="detail.hospital_name"
            />
          </div>
        </div>

        <TableDefinition
          :items="aboutTableData"
          class="hospitalCassetteSeminar_table"
        />

        <p class="hospitalCassetteSeminar_texts">
          {{ aboutText }}
        </p>

        <HospitalTag
          v-if="data.features.length > 0"
          class="hospitalCassetteSeminar_feature"
          :tags="data.features"
        />

        <div class="hospitalCassetteSeminar_btns" v-if="!enableRequest">
          <Button
            :href="
              `/search/hospital/detail/${detail.hospital_id}?tab=latter&id=${detail.s_training_id}`
            "
            :label="
              detail.active_flg === 3
                ? 'プログラムの詳細を見る'
                : '掲載が終了しました'
            "
            :handleClick="
              () =>
                $router.push(
                  `/search/hospital/detail/${detail.hospital_id}?tab=latter&id=${detail.s_training_id}`
                )
            "
            :disabled="detail.active_flg !== 3"
            primary
          />
          <FavoriteButton
            :label="favBtnLabel"
            :is-active="isFavorite"
            :handleClick="() => clickFavBtn()"
          />
        </div>
      </div>
    </div>

    <button
      v-if="!enableRequest"
      class="hospitalCassetteSeminar_fav"
      :class="{ '-active': isFavorite }"
      type="button"
      @click="clickFavBtn()"
    >
      {{ favBtnLabel }}
    </button>

    <!-- <div class="hospitalCassetteSeminar_date" v-if="detail.postperiod_dt">
      <dl>
        <dt>掲載期限</dt>
        <dd>{{ formattedPostperiodDt }}</dd>
      </dl>
    </div> -->

    <div class="hospitalCassetteSeminar_checkbox" v-if="canRequestCheck">
      <input
        type="checkbox"
        :name="`${detail.s_training_id}_check`"
        :id="`${detail.s_training_id}_check`"
        :value="detail.s_training_id"
        @change="changeInput($event)"
      /><label
        :for="`${detail.s_training_id}_check`"
        class="hospitalCassetteSeminar_checkboxLabel"
      ></label>
    </div>
  </li>
</template>

<script lang="ts">
import { defineComponent, SetupContext, reactive, computed } from 'vue'
import { useStore } from 'vuex'
import Button from '@/components/elements/Button.vue'
import FavoriteButton from '@/components/elements/FavoriteButton.vue'
import HospitalLabel from '@/components/elements/HospitalLabel.vue'
import TableDefinition, {
  Definition
} from '@/components/elements/TableDefinition.vue'
import HospitalTag from '@/components/elements/HospitalTag.vue'
import { ActionHistory } from '@/types/StoreState'
import { numberFormatAddComma } from '@/utils/commonMethods'

export default defineComponent({
  name: 'HospitalCassetteSeminar',

  props: {
    detail: {
      type: Object,
      default: () => ({})
    },
    enableRequest: {
      type: Boolean,
      default: false
    },
    isShowRequestBtn: {
      type: Boolean,
      default: false
    }
  },

  components: {
    Button,
    FavoriteButton,
    HospitalLabel,
    HospitalTag,
    TableDefinition
  },

  setup(props, context: SetupContext) {
    const store = useStore()
    const data = reactive({
      labels: props.detail.program_subjects,
      features: props.detail.feature_tag
        ? props.detail.feature_tag.map((el: string) => {
            return {
              label: el,
              disabled: false
            }
          })
        : []
    })

    const aboutTableData = computed((): Definition[] => {
      return [
        {
          dt: '当科病床数',
          dd:
            props.detail.department_bed_num !== null
              ? `${numberFormatAddComma(props.detail.department_bed_num)}`
              : 0,
          unit: '床'
        },
        {
          dt: '研修医数',
          dd: props.detail.resident_num ?? 0,
          unit: '人'
        },
        {
          dt: '当科指導医数',
          dd: props.detail.instructor_num ?? 0,
          unit: '人'
        },
        {
          dt: '募集人数',
          dd: props.detail.recruitment_num ?? 0,
          unit: '人'
        }
      ]
    })

    // お気に入り判定
    const isFavorite = computed(() => {
      if (!store.state.isLogin) return false

      return store.state.userInfo.s_favorite_history.some(
        (el: ActionHistory) => el.s_training_id == props.detail.s_training_id
      )
    })

    const clickFavBtn = () => {
      if (isFavorite.value) {
        store.dispatch('removeFavorite', {
          id: props.detail.hospital_id,
          sId: props.detail.s_training_id
        })
      } else {
        store.dispatch('addFavorite', {
          id: props.detail.hospital_id,
          sId: props.detail.s_training_id
        })
      }
    }

    const favBtnLabel = computed(() => {
      return isFavorite.value ? 'お気に入り登録済み' : 'お気に入りに保存'
    })

    const aboutText = computed(() => {
      const text = props.detail.overview_list || ''
      return text?.length > 77 ? text.substring(0, 76) + '…' : text
    })

    const canRequestCheck = computed(() => {
      if (
        props.enableRequest &&
        props.detail.paid_flg === 1 &&
        props.detail.active_flg === 3
      ) {
        return true
      } else {
        return false
      }
    })

    const changeInput = (e: any) => {
      context.emit('call-input-change', {
        value: e.target.value,
        checked: e.target.checked
      })
    }

    return {
      data,
      isFavorite,
      clickFavBtn,
      favBtnLabel,
      canRequestCheck,
      aboutText,
      changeInput,
      aboutTableData
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;

.hospitalCassetteSeminar {
  position: relative;
  border-radius: 5px;
  background: #fff;
  border: 1px solid rgb(228, 228, 228);
  box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.08);
  font-size: 1.3rem;

  & + & {
    margin-top: 15px;
    @include mixins.media() {
      margin-top: 24px;
    }
  }

  &_fav {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    // text-align: center;
    background-color: #f5f5f5;
    padding: 10px;
    font-size: 1.3rem;
    @include mixins.media() {
      display: none;
    }

    &::before {
      content: '';
      display: block;
      margin-right: 8px;
      width: 20px;
      height: 18px;
      background-image: url('~@/assets/icon/heart.svg');
      background-size: 20px 18px;
    }

    &.-active::before {
      background-image: url('~@/assets/icon/heart_on.svg');
      background-size: 20px 18px;
    }
  }

  &_inner {
    padding: 20px;
    position: relative;
    @include mixins.media() {
      padding-top: 25px;
      padding-bottom: 25px;
    }
  }

  &_hospital {
    font-size: 1.3rem;
    font-weight: 500;
    line-height: 1.5;
    @include mixins.media() {
      font-size: 1.4rem;
    }
  }

  &_ttl {
    font-size: 1.8rem;
    font-weight: 500;
    line-height: 1.5;
    margin-bottom: 10px;
  }

  &_tag {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;

    > li {
      font-size: 1rem;
      font-weight: 500;
      background-color: #fffccb;
      padding: 4px;
      line-height: 1;
      margin-right: 4px;
      margin-bottom: 4px;
    }
  }

  &_info {
    margin-top: 15px;
    overflow: hidden;
    position: relative;
    margin-bottom: 20px;
    @include mixins.media() {
      display: flex;
    }
  }

  &_address {
    display: flex;
    align-items: center;
    font-size: 1.3rem;
    @include mixins.media() {
      font-size: 1.4rem;
      margin-right: 40px;
    }
    &::before {
      content: '';
      margin-left: 2px;
      display: inline-block;
      margin-right: 10px;
      width: 12px;
      height: 18px;
      background-image: url('~@/assets/icon/address.svg');
    }
  }

  &_class {
    display: flex;
    align-items: center;
    font-size: 1.3rem;
    @include mixins.media() {
      font-size: 1.4rem;
    }
    &::before {
      content: '';
      display: inline-block;
      margin-right: 8px;
      width: 18px;
      height: 16px;
      background-image: url('~@/assets/icon/hospital.svg');
    }
  }

  &_table {
    margin-bottom: 17px;
    @include mixins.media() {
      width: 67.7%;
    }
  }

  &_texts {
    font-size: 1.3rem;
    line-height: 1.5;
    margin-bottom: 27px;
    @include mixins.media() {
      font-size: 1.4rem;
      line-height: 1.7;
      margin-bottom: 23px;
    }
  }

  &_img {
    width: 70px;
    position: absolute;
    right: 0px;
    top: 0;
    @include mixins.media() {
      display: none;
    }
    &.-isPC {
      right: 20px;
      top: 24px;
      width: 25.2%;
      display: none;
      @include mixins.media() {
        display: block;
      }
    }
  }

  &_feature {
    margin-top: 20px;

    @include mixins.media() {
      margin-top: 0;
      flex: 1;
    }
  }

  &_btns {
    margin-top: 24px;

    @include mixins.media() {
      display: flex;
      justify-content: center;
    }

    * {
      max-width: 71%;
      height: 40px;
      min-height: 40px;

      @include mixins.media() {
        margin-left: 0;
        margin-right: 0;
        max-width: 215px;
      }
    }

    .button + .button {
      margin-top: 16px;

      @include mixins.media() {
        display: flex;
        margin-top: 0;
        margin-left: 20px;
      }
    }

    @include mixins.mediaMax() {
      > .button:last-child {
        display: none;
      }
    }
  }

  &_header {
    @include mixins.media() {
      width: calc(74.8% - 20px);
    }
  }

  &_header.hasCheckbox {
    margin-left: 60px;
  }

  &_checkbox {
    position: absolute;
    top: 40px;
    left: 30px;
    width: 20px;
    height: 20px;
    // transform: translate(30px, 80px);

    & > input[type='checkbox'] {
      display: none;
    }
  }

  &_checkboxLabel {
    position: relative;
    transition: background 0.1s ease, border 0.1s ease;

    &::before {
      display: inline-block;
      content: '';
      width: 20px;
      height: 20px;
      background-color: var.$color-border-gray;
      border-radius: 4px;
      flex-shrink: 0;
      transition: background 0.1s ease;
    }

    &::after {
      opacity: 0;
      position: absolute;
      top: 0;
      left: 0;
      display: inline-block;
      content: '';
      width: 12px;
      height: 8px;
      border-left: 2px solid #fff;
      border-bottom: 2px solid #fff;
      transform: rotate(-45deg) translate(4px, 2px);
      transition: opacity 0.1s ease;
    }

    .hospitalCassetteSeminar_checkbox > input[type='checkbox']:checked + & {
      &::before {
        background-color: var.$color-main;
      }
      &::after {
        opacity: 1;
      }
    }
  }

  // &_date {
  //   margin-top: 10px;
  //   margin-bottom: 20px;
  //   font-size: 1.2rem;
  //   text-align: center;

  //   > dl {
  //     &::before {
  //       display: inline-block;
  //       content: '';
  //       background: url('~@/assets/images/icon/time.svg') no-repeat;
  //       background-size: 100% 100%;
  //       width: 17px;
  //       height: 17px;
  //       margin-right: 5px;
  //       vertical-align: -4px;
  //     }

  //     > dt {
  //       display: inline;

  //       &::after {
  //         content: '：';
  //       }
  //     }
  //     > dd {
  //       display: inline;
  //     }
  //   }
  // }
}
</style>
